import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '@/hooks';
import { cn, shortenHex, shortenIfEmail } from '@/utils';
import { useSwitchAccount } from '@mysten/dapp-kit';
import type { Wallet } from '@mysten/wallet-standard';
import { Link, useNavigate } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronRight, Copy, LogOut } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import { Button } from './ui';
import { AudioControls } from './audio-controls';
import { ThemeToggle } from './theme-toggle';
import { SuiFrenPortrait } from '@/components/suifren/SuiFrenPortrait';
import { WalletIcon } from './wallet-icon';
import { useCampaignModalStore } from '@/stores/campaign-modal-store';
import { useOneTimeFlag } from '@/hooks/use-one-time-flag';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';
import { useResolvedSuinsName } from '@/hooks/use-suins-resolution';

type AccountDropdownProps = { logoutBtnOnClick: () => void; triggerClassName?: string; hideThemeToggle?: boolean };
export function AccountDropdown({ logoutBtnOnClick, triggerClassName, hideThemeToggle = false }: AccountDropdownProps) {
	const { address, currentAccount, accounts, walletProvider, primaryWallet } = useAuth();
	const { mutate: switchAccount } = useSwitchAccount();

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = React.useRef<number | null>(null);

	const { data: nameserver } = useResolvedSuinsName(address, { enabled: Boolean(address) });

	const resolvedName = typeof nameserver === 'string' && nameserver !== 'N/A' && nameserver.trim().length > 0 ? nameserver : undefined;

	const fallbackLabel =
		(currentAccount?.label && shortenIfEmail(currentAccount.label)) || (currentAccount?.address ? shortenHex(currentAccount.address) : undefined);

	const identityLabel = resolvedName ?? fallbackLabel ?? 'Connected account';
	const identityAddress = address ? shortenHex(address) : null;

	const selectedAccountAddress = currentAccount?.address ?? '';

	const openCampaignModal = useCampaignModalStore((state) => state.open);
	const promoCtaFlag = useOneTimeFlag(CAMPAIGN_CFG.promoButton?.id);
	const navigate = useNavigate();

	const accountShortcuts: Array<{ label: string; to?: string; onSelect?: () => void }> = [{ label: 'Profile', to: '/profile' }];

	if (CAMPAIGN_CFG.promoButton) {
		accountShortcuts.push({
			label: 'Slush Wallet Campaign',
			onSelect: () => {
				if (CAMPAIGN_CFG.promoButton?.to) {
					navigate({ to: CAMPAIGN_CFG.promoButton.to });
				} else {
					openCampaignModal();
				}
				if (!promoCtaFlag.hasSeen) {
					promoCtaFlag.markSeen();
				}
				setIsDropdownOpen(false);
			},
		});
	}

	const triggerLabel = identityLabel ? `Account menu for ${identityLabel}` : 'Account menu';

	const accountSeed = (identityLabel ?? 'suigar').trim();

	const providerBadge = React.useMemo(() => {
		const iconMap: Record<string, Wallet['icon']> = {
			google:
				'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iIzQyODVGNCIgZD0iTTIyLjU2IDEyLjI1YzAtLjc4LS4wNy0xLjUzLS4yLTIuMjVIMTJ2NC4yNmg1LjkyYy0uMjYgMS4zNy0xLjA0IDIuNTMtMi4yMSAzLjMxdjIuNzdoMy41N2MyLjA4LTEuOTIgMy4yOC00Ljc0IDMuMjgtOC4wOSIvPjxwYXRoIGZpbGw9IiMzNEE4NTMiIGQ9Ik0xMiAyM2MyLjk3IDAgNS40Ni0uOTggNy4yOC0yLjY2bC0zLjU3LTIuNzdjLS45OC42Ni0yLjIzIDEuMDYtMy43MSAxLjA2LTIuODYgMC01LjI5LTEuOTMtNi4xNi00LjUzSDIuMTh2Mi44NEMzLjk5IDIwLjUzIDcuNyAyMyAxMiAyMyIvPjxwYXRoIGZpbGw9IiNGQkJDMDUiIGQ9Ik01Ljg0IDE0LjA5Yy0uMjItLjY2LS4zNS0xLjM2LS4zNS0yLjA5cy4xMy0xLjQzLjM1LTIuMDlWNy4wN0gyLjE4QzEuNDMgOC41NSAxIDEwLjIyIDEgMTJzLjQzIDMuNDUgMS4xOCA0LjkzbDIuODUtMi4yMnoiLz48cGF0aCBmaWxsPSIjRUI0MzM1IiBkPSJNMTIgNS4zOGMxLjYyIDAgMy4wNi41NiA0LjIxIDEuNjRsMy4xNS0zLjE1QzE3LjQ1IDIuMDkgMTQuOTcgMSAxMiAxIDcuNyAxIDMuOTkgMy40NyAyLjE4IDcuMDdsMy42NiAyLjg0Yy44Ny0yLjYgMy4zLTQuNTMgNi4xNi00LjUzIi8+PC9zdmc+',
			twitch:
				'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48ZyBmaWxsPSIjNjQ0MWE0Ij48cGF0aCBkPSJNODMuMiAzOC40IDUxLjIwMiA3MC4zOTh2MTE1LjIwNGgzOC40MDRWMjE3LjZsMzEuOTk5LTMxLjk5OGgyNS41OEwyMDQuNzk5IDEyOFYzOC40em0xMDguNzk3IDgzLjIwNS0yNS41OTIgMjUuNTkyaC0yNS42MDNsLTIyLjQgMjIuNHYtMjIuNEg4OS42MDZWNTEuMjAyaDEwMi4zOXoiLz48cGF0aCBkPSJNMTcyLjggNzMuNjAyaC0xMi44MDJ2MzguMzgySDE3Mi44em0tMzUuMjAyIDBoLTEyLjgwMXYzOC4zODJoMTIuODAxeiIvPjwvZz48L3N2Zz4=',
		};

		return {
			id: primaryWallet?.id,
			name: walletProvider ?? '',
			icon: iconMap[walletProvider ?? ''] || primaryWallet?.icon,
		};
	}, [primaryWallet, walletProvider]);

	const handleCopy = React.useCallback(() => {
		if (!address) return;
		navigator.clipboard
			.writeText(address)
			.then(() => {
				setCopied(true);
				if (copyTimeoutRef.current) {
					window.clearTimeout(copyTimeoutRef.current);
				}
				copyTimeoutRef.current = window.setTimeout(() => {
					setCopied(false);
					copyTimeoutRef.current = null;
				}, 2000);
			})
			.catch((error) => {
				console.error('Failed to copy address', error);
			});
	}, [address]);

	React.useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) {
				window.clearTimeout(copyTimeoutRef.current);
			}
		};
	}, []);

	const handleLogout = () => {
		setIsDropdownOpen(false);
		logoutBtnOnClick();
	};

	const handleWalletChange = (value: string) => {
		if (!accounts || value === selectedAccountAddress) {
			return;
		}
		const nextAccount = accounts && accounts.find((account) => account.address === value);
		if (nextAccount) {
			switchAccount({ account: nextAccount });
			setIsDropdownOpen(false);
		}
	};

	return (
		<DropdownMenu open={isDropdownOpen} onOpenChange={(open) => setIsDropdownOpen(open)}>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className={cn(triggerClassName, 'gap-1')} aria-label={triggerLabel}>
					<SuiFrenPortrait seed={accountSeed} size="xs" className="bg-primary/10" zoom={1.45} focusOffset={{ y: -18 }} />
					<span className="hidden truncate text-foreground md:inline">{identityLabel}</span>
					<ChevronDown className={cn('size-4 transition-transform', isDropdownOpen ? 'rotate-180' : '')} />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={8}
				className="w-[320px] overflow-hidden rounded-2xl border border-border/50 bg-background/95 p-0 shadow-xl backdrop-blur-xl"
			>
				<div className="flex flex-col gap-4 p-4">
					<div className="flex items-start gap-3">
						<Link
							to="/profile"
							className="group rounded-full outline-none transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-primary"
							aria-label="View profile"
							onClick={() => setIsDropdownOpen(false)}
						>
							<SuiFrenPortrait seed={accountSeed} size="sm" className="border border-border/50 bg-primary/10" zoom={1.45} focusOffset={{ y: -18 }} />
						</Link>
						<div className="flex flex-1 flex-col gap-1">
							<div className="flex items-start justify-between gap-2">
								<div className="flex min-w-0 flex-1 flex-col gap-1">
									{accounts && accounts.length > 1 ? (
										<Select value={selectedAccountAddress} onValueChange={handleWalletChange}>
											<SelectTrigger className="h-9 w-full min-w-0 justify-between rounded-lg border border-border/60 bg-background/90 px-3 text-left text-sm font-semibold text-foreground">
												<SelectValue placeholder="Select account" />
											</SelectTrigger>
											<SelectContent>
												{accounts.map((account) => (
													<SelectItem key={account.address} value={account.address}>
														{account.address?.toLowerCase() === address?.toLowerCase() && resolvedName
															? resolvedName
															: account.label
																? shortenIfEmail(account.label)
																: shortenHex(account.address)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : (
										<span className="truncate text-sm font-semibold text-foreground">{identityLabel}</span>
									)}
								</div>
								<Button
									variant="ghost"
									size="icon"
									className="p-0! shrink-0 text-muted-foreground transition-colors hover:text-foreground rounded-none"
									onClick={handleLogout}
									aria-label="Log out"
								>
									<LogOut />
								</Button>
							</div>
							{identityAddress ? (
								<div className="flex items-center justify-between gap-2">
									<button
										type="button"
										onClick={handleCopy}
										className="inline-flex -ml-2 items-center gap-1.5 rounded-lg border border-transparent px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-border/50 hover:text-foreground"
									>
										<span className="font-mono">{identityAddress}</span>
										{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
									</button>
									{providerBadge ? (
										<span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
											<WalletIcon key={providerBadge.id || providerBadge.name} item={providerBadge} size="xs" />
											{providerBadge.name}
										</span>
									) : null}
								</div>
							) : null}
						</div>
					</div>

					<nav className="grid gap-1.5">
						{accountShortcuts.map((shortcut) =>
							shortcut.to ? (
								<Link
									key={shortcut.label}
									to={shortcut.to}
									className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:border-border/50 hover:bg-primary/10 hover:text-foreground"
									onClick={() => setIsDropdownOpen(false)}
								>
									<span>{shortcut.label}</span>
									<ChevronRight className="h-4 w-4 text-muted-foreground" />
								</Link>
							) : (
								<button
									key={shortcut.label}
									type="button"
									onClick={() => shortcut.onSelect?.()}
									className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 text-left text-sm font-medium text-foreground/80 transition-colors hover:border-border/50 hover:bg-primary/10 hover:text-foreground"
								>
									<span>{shortcut.label}</span>
									<ChevronRight className="h-4 w-4 text-muted-foreground" />
								</button>
							),
						)}
					</nav>
				</div>

				<div className="flex items-center justify-between border-t border-border/40 bg-background/80 px-4 py-3">
					<AudioControls />
					{!hideThemeToggle ? <ThemeToggle /> : null}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
